<template>
  <div class="settings-panel" role="dialog" aria-label="Settings">
    <div
      class="settings-panel__viewport"
      ref="panelBody"
      tabindex="-1"
    >
      <header class="settings-panel__header" role="banner">
        <h2 class="settings-panel__title">Settings</h2>
      </header>

      <div class="settings-panel__body" role="document">
        <section class="settings-section" aria-labelledby="appearance-heading">
          <label class="settings-label">Dark mode</label>
          <div class="settings-control">
            <v-radio-group
              v-model="mode"
              column
              aria-label="Theme preference"
              class="pref-radio-group"
            >
              <v-radio label="Use browser setting" value="auto" />
              <v-radio label="Light"               value="light" />
              <v-radio label="Dark"                value="dark"  />
            </v-radio-group>
          </div>
        </section>

        <!-- Data Retention — Administrator only -->
        <section
          v-if="isAdmin"
          class="settings-section"
          aria-labelledby="retention-heading"
        >
          <h3 id="retention-heading" class="settings-section-heading">
            Data Retention
          </h3>
          <p class="settings-description">
            Automatically delete user accounts that have been inactive for a
            specified number of days. Administrator accounts are never deleted.
          </p>

          <div v-if="retentionLoading" class="settings-loading">
            <v-progress-circular indeterminate size="24" />
          </div>

          <template v-else>
            <div class="settings-control retention-toggle">
              <v-switch
                v-model="retentionEnabled"
                label="Enable automatic user deletion"
                color="primary"
                hide-details
                density="compact"
              />
            </div>

            <div class="settings-control retention-threshold" v-if="retentionEnabled">
              <label class="settings-label" for="threshold-input">
                Delete users inactive for more than
              </label>
              <div class="threshold-input-row">
                <v-text-field
                  id="threshold-input"
                  v-model.number="retentionDays"
                  type="number"
                  :min="30"
                  step="1"
                  :rules="[thresholdRule]"
                  suffix="days"
                  density="compact"
                  style="max-width: 180px"
                  hide-details="auto"
                  @blur="clampRetentionDays"
                  @keydown="onThresholdKeydown"
                />
              </div>
            </div>

            <div class="retention-actions">
              <v-btn
                color="primary"
                variant="flat"
                size="small"
                :loading="retentionSaving"
                :disabled="!retentionDirty"
                @click="saveRetentionPolicy"
              >
                Save
              </v-btn>
              <span v-if="retentionSaved" class="retention-saved-msg">Saved</span>
              <span v-if="retentionError" class="retention-error-msg">{{ retentionError }}</span>
            </div>
          </template>
        </section>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, onMounted, computed } from 'vue'
import { useThemePreference } from '@/composables/useThemePreference'
import { useAuthenticationStore } from '@/stores/authentication'
import GetRetentionPolicyQuery from '@/common/api/queries/RetentionPolicy/GetRetentionPolicyQuery'
import UpdateRetentionPolicyCommand from '@/common/api/commands/RetentionPolicy/UpdateRetentionPolicyCommand'

export default defineComponent({
  name: 'SettingsPanel',
  emits: ['close'],

  setup(_, { emit }) {
    const { mode } = useThemePreference()
    const panelBody = ref<HTMLElement | null>(null)
    const authStore = useAuthenticationStore()

    const isAdmin = computed(() => authStore.user?.role === 'Administrator')

    // Retention policy state
    const retentionLoading = ref(false)
    const retentionSaving = ref(false)
    const retentionSaved = ref(false)
    const retentionError = ref('')
    const retentionEnabled = ref(false)
    const retentionDays = ref(365)

    // Track original values to detect changes
    const originalEnabled = ref(false)
    const originalDays = ref(365)

    const retentionDirty = computed(() =>
      retentionEnabled.value !== originalEnabled.value ||
      retentionDays.value !== originalDays.value
    )

    const thresholdRule = (v: number) =>
      v >= 30 || 'Minimum is 30 days'

    function clampRetentionDays() {
      if (retentionDays.value == null || retentionDays.value < 30) {
        retentionDays.value = 30
      }
      retentionDays.value = Math.floor(retentionDays.value)
    }

    function onThresholdKeydown(e: KeyboardEvent) {
      // Block minus, period, 'e' to keep it a positive integer
      if (['-', '.', 'e', 'E'].includes(e.key)) {
        e.preventDefault()
      }
    }

    async function loadRetentionPolicy() {
      if (!isAdmin.value) return
      retentionLoading.value = true
      retentionError.value = ''
      try {
        const query = new GetRetentionPolicyQuery()
        const response = await query.Execute()
        if (response.ok) {
          const data = await response.json()
          retentionEnabled.value = data.isEnabled
          retentionDays.value = data.inactivityThresholdDays
          originalEnabled.value = data.isEnabled
          originalDays.value = data.inactivityThresholdDays
        }
      } catch {
        retentionError.value = 'Failed to load retention settings'
      } finally {
        retentionLoading.value = false
      }
    }

    async function saveRetentionPolicy() {
      if (retentionDays.value < 30) {
        retentionError.value = 'Minimum threshold is 30 days'
        return
      }
      retentionSaving.value = true
      retentionSaved.value = false
      retentionError.value = ''
      try {
        const command = new UpdateRetentionPolicyCommand(
          retentionEnabled.value,
          retentionDays.value
        )
        const response = await command.Execute()
        if (response.ok) {
          const data = await response.json()
          retentionEnabled.value = data.isEnabled
          retentionDays.value = data.inactivityThresholdDays
          originalEnabled.value = data.isEnabled
          originalDays.value = data.inactivityThresholdDays
          retentionSaved.value = true
          setTimeout(() => { retentionSaved.value = false }, 3000)
        } else {
          retentionError.value = 'Failed to save retention settings'
        }
      } catch {
        retentionError.value = 'Failed to save retention settings'
      } finally {
        retentionSaving.value = false
      }
    }

    onMounted(() => {
      nextTick(() => {
        panelBody.value?.focus({ preventScroll: true })
      })
      loadRetentionPolicy()
    })

    function requestClose() {
      emit('close')
    }

    return {
      mode,
      panelBody,
      requestClose,
      isAdmin,
      retentionLoading,
      retentionSaving,
      retentionSaved,
      retentionError,
      retentionEnabled,
      retentionDays,
      retentionDirty,
      thresholdRule,
      clampRetentionDays,
      onThresholdKeydown,
      saveRetentionPolicy
    }
  }
})
</script>

<style scoped lang="scss">
.settings-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

.settings-panel__viewport {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.settings-panel__header {
  padding: 16px 20px;
  border-bottom: 1px solid rgb(var(--v-theme-border-color, 210,219,219));
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.settings-panel__title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
}

.settings-panel__body {
  padding: 16px 20px;
  overflow: auto;
  flex: 1 1 auto;
}

.settings-section {
  margin-top: 12px;
  margin-bottom: 20px;
}

.settings-section-heading {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.settings-description {
  font-size: 13px;
  opacity: 0.75;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.settings-label {
  min-width: 120px;
  font-weight: 500;
  font-size: 16px;
  padding-top: 6px;
}

.settings-control {
  flex: 1;
}

.settings-loading {
  padding: 12px 0;
}

.retention-toggle {
  margin-bottom: 8px;
}

.retention-threshold {
  margin-bottom: 12px;

  .settings-label {
    font-size: 14px;
    font-weight: 400;
    display: block;
    margin-bottom: 4px;
  }
}

.threshold-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.retention-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.retention-saved-msg {
  font-size: 13px;
  color: rgb(var(--v-theme-success, 76, 175, 80));
}

.retention-error-msg {
  font-size: 13px;
  color: rgb(var(--v-theme-error, 244, 67, 54));
}
</style>
